import {moveItemInArray} from '@angular/cdk/drag-drop';
import {
    computed,
    effect,
    inject,
    Injectable,
    Provider,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {NonNullableFormBuilder, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {DateTime} from 'luxon';
import {catchError, finalize, map, of, switchMap} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {z} from 'zod';
import {AppValidators, TRANSLATE} from '@brunata-angular/util';
import {injectHoldsAdvancedDelivery} from '../shared/services/advanced-delivery';
import {csvExportFormat, exportFormats} from '../shared/models/output-format.model';
import {
    defaultExportColumns,
    dismountedColumnId,
    exportColumnFields,
    ExportColumnId,
    exportColumnIdSchema,
    ExportColumnState,
    headerRowModeSchema,
    HeaderRowMode,
    exportColumnHeadingKey,
    meterValueAndUnitLabelKey,
    OrderedExportColumn,
    reconcileExportColumns,
    unitPlacementSchema,
    UnitPlacement,
} from '../shared/models/export-columns.model';
import {
    bindExportFileFormat,
    exportFileFormatForm,
    ExportFileFormatForm,
    exportFileFormatFormValue,
    resetExportFileFormat,
} from '../shared/models/export-file-format.model';
import {FilePreviewContent} from '../file-preview/file-preview.model';
import {formattedValueKind} from '../file-preview/exported-value.model';
import {previewContent, PreviewColumnChoice} from '../file-preview/export-preview.model';
import {
    maxSavedExportNameLength,
    SavedExport,
    SavedExportChannel,
    SavedExportColumn,
    SavedExportCreateRequest,
    savedExportFileFormat,
    savedExportFileName,
    sftpDeliveryFileName,
    savedExportScheduleFields,
    savedExportScheduleValue,
    SavedExportTestRequest,
    SavedExportUsage,
    savedExportUsage,
    savedExportUsageSchema,
    toSavedExportChannelRequest,
    toSavedExportColumnConfig,
} from '../saved-exports/saved-export.model';
import {
    ExportLimits,
    exportLimits,
    noExportLimits,
    sftpConnectionRemovedLimits,
} from '../steps/step-menu/export-limits.model';
import {
    SavedExportDraft,
    clearStoredDraft,
    emptySavedExportDraft,
    readStoredDraft,
    scheduleDraftValue,
    scheduleFormValue,
    writeStoredDraft,
} from './customized-exports-draft';
import {
    defaultExportName,
    EXPORT_FORM,
    ExportForm,
    exportScheduleGroup,
    ExportTestSource,
    sftpConnectionRemoval,
} from '../shared/models/export-form.model';
import {
    ReadingResolution,
    maxDaysBackInTime,
    minDaysBackInTime,
    readingResolutionSchema,
} from '../shared/models/reading-shape.model';
import {SavedExportsService} from '../saved-exports/saved-exports.service';
import {exportTestProviders} from '../shared/services/export-test.service';
import {MeterTypesService} from '../steps/meter-types-step/meter-types.service';
import {
    ChannelsService,
    CustomizedExportSuperTypeChannels,
    meterModelKeyOf,
} from '../steps/meter-scope/channels.service';
import {SystemsService} from '../steps/systems-step/systems.service';
import {endedMonthsWindowDays} from '../steps/schedule-step/next-deliveries.model';
import {
    buildMeterScope,
    includedChannelLessSuperTypes,
    isChannelLessTicked,
    selectedChannelLessSuperTypes,
    MeterScopeAvailability,
    meterScopeAvailability,
    meterScopeChannels,
    meterScopeSelectionFromChannels,
    modelIdsForKeys,
} from '../steps/meter-scope/meter-scope.model';
import {relevantSystemRows} from '../steps/systems-step/systems-step.model';
import {MeterTypeCard} from '../steps/meter-types-step/meter-types-step.component';
import {FlowSlot} from '../shared/models/export-flow-slots.model';
import {controlStepStatus, StepStatus} from '../steps/step-focus/step-state.model';

type StoredModelKeys = {
    selected: string[];
    offered: string[];
};

@Injectable()
export class CustomizedExportsFormService implements ExportTestSource {
    private readonly translate = inject(TRANSLATE);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly savedExportsService = inject(SavedExportsService);
    private readonly toastrService = inject(ToastrService);
    private readonly router = inject(Router);
    private readonly superTypesService = inject(MeterTypesService);
    private readonly channelsService = inject(ChannelsService);
    private readonly systemsService = inject(SystemsService);

    private readonly stored = readStoredDraft();

    private readonly isEditing = signal(false);

    readonly isSaving = signal(false);

    readonly selectedUsage = signal<SavedExportUsage>(this.stored.usage);

    readonly selectedSuperTypes = signal<string[]>(this.stored.superTypes);

    readonly selectedModelIds = signal<string[]>([]);

    readonly selectedChannelIds = signal<string[]>(this.stored.channelIds);

    private readonly offeredModelIds = signal<string[]>([]);

    private readonly offeredChannelIds = signal<string[]>(this.stored.offeredChannelIds);

    private readonly selectedChannelLess = signal<Record<string, boolean>>(this.stored.channelLess);

    // A model id carries the vendor name, so the draft keys its models without it. The keys stand in
    // for the two model signals until a response can say which models they mean.
    private readonly storedModelKeys = signal<StoredModelKeys | null>({
        selected: this.stored.modelKeys,
        offered: this.stored.offeredModelKeys,
    });

    private readonly scopeAwaitingRestore = signal<ReadonlyArray<SavedExportChannel> | null>(null);

    private readonly superTypesAwaitingRestore = signal<ReadonlyArray<string>>([]);

    private readonly systemsAwaitingAutoInclude = signal(false);

    private readonly offeredSystemNos = signal<string[]>([]);

    // undefined = still loading, null = the fetch failed, [] = none available.
    readonly availableSuperTypes = toSignal(
        this.superTypesService.getAvailableSuperTypes().pipe(catchError(() => of(null))),
    );

    readonly meterTypeCards = computed<ReadonlyArray<MeterTypeCard> | null | undefined>(() => {
        const available = this.availableSuperTypes();
        if (available == null) {
            return available;
        }
        const selected = this.selectedSuperTypes();
        return available.map(meterType => ({
            meterSuperTypeNo: meterType.meterSuperTypeNo,
            nameKey: `mappers_meterType.${meterType.meterSuperTypeNo}`,
            selected: selected.includes(meterType.meterSuperTypeNo),
        }));
    });

    private readonly saveAttempted = signal(false);

    private readonly availableChannels = toSignal(
        toObservable(this.selectedSuperTypes).pipe(
            switchMap(superTypes =>
                superTypes.length === 0
                    ? of<CustomizedExportSuperTypeChannels[]>([])
                    : this.channelsService
                          .getAvailableChannels(superTypes)
                          .pipe(catchError(() => of(null))),
            ),
        ),
    );

    readonly meterScope = computed(() =>
        buildMeterScope(
            this.availableChannels(),
            this.selectedSuperTypes(),
            this.selectedModelIds(),
            this.selectedChannelIds(),
            this.selectedChannelLess(),
        ),
    );

    private readonly channelLessSuperTypes = computed(() =>
        selectedChannelLessSuperTypes(
            this.availableChannels(),
            this.meterScope(),
            this.selectedSuperTypes(),
            this.selectedChannelLess(),
        ),
    );

    private readonly storedChannels = computed(() => {
        const awaitingRestore = this.scopeAwaitingRestore();
        return awaitingRestore === null
            ? meterScopeChannels(
                  this.availableChannels(),
                  this.selectedSuperTypes(),
                  this.selectedModelIds(),
                  this.selectedChannelIds(),
              )
            : awaitingRestore;
    });

    // Models and channels are picked as two independent lists, but what gets saved is their cross
    // product, so the guard has to count the pairs. Counting the lists lets an empty scope through.
    readonly hasSelectedChannels = computed(
        () => this.storedChannels().length > 0 || this.channelLessSuperTypes().length > 0,
    );

    readonly channelsUnavailable = computed(() => this.availableChannels() === null);

    readonly channelsOfferNone = computed(() => {
        const scope = this.meterScope();
        return scope.offersNoChannels && scope.channelLess.length === 0;
    });

    private readonly availableSystems = toSignal(
        this.systemsService.getAvailableSystems().pipe(catchError(() => of(null))),
    );

    readonly relevantSystems = computed(() =>
        relevantSystemRows(this.availableSystems(), this.selectedSuperTypes()),
    );

    // save() blocks while the super types, the channels or the systems are still loading, so the
    // button has to say so rather than swallow the click.
    readonly saveDisabled = computed(
        () =>
            this.isSaving() ||
            this.availableSuperTypes() === undefined ||
            this.meterScope().loading ||
            this.relevantSystems() === undefined,
    );

    readonly meterSelectionBlocked = computed(() => {
        const available = this.availableSuperTypes();
        if (available == null || available.length === 0) {
            return true;
        }
        if (this.channelsUnavailable() && !this.restoredScopeUnchanged()) {
            return true;
        }
        if (this.channelsTickedWithoutModels()) {
            return true;
        }
        return !this.hasSelectedChannels() && !this.channelsOfferNone();
    });

    readonly saveBlockedReason = computed<string | null>(() => {
        if (!this.saveAttempted()) {
            return null;
        }
        if (this.boundFileFormat.status() === 'INVALID') {
            return 'customizedexports_export_save_fileformat_invalid';
        }
        if (this.sftpDeliveryUnsettled()) {
            return 'customizedexports_export_save_sftp_connection_required';
        }
        if (this.sftpDeliveryWithoutService()) {
            return 'savedexport_sftp_requires_advanced_delivery';
        }
        if (this.meterSelectionBlocked()) {
            return this.meterSelectionBlockedReason();
        }
        return this.systemsRequired() ? this.systemsRequiredReason() : null;
    });

    private readonly systemsRequiredReason = computed<string>(() => {
        const systems = this.relevantSystems();
        if (systems === null) {
            return 'customizedexports_export_step_systems_load_error';
        }
        if (systems?.length === 0) {
            return 'customizedexports_export_step_systems_empty';
        }
        return 'customizedexports_export_systems_required';
    });

    private readonly meterSelectionBlockedReason = computed<string | null>(() => {
        const available = this.availableSuperTypes();
        if (available === null) {
            return 'customizedexports_export_step_supertypes_load_error';
        }
        if (available === undefined) {
            return null;
        }
        if (available.length === 0) {
            return 'customizedexports_export_save_no_supertypes';
        }
        if (this.channelsUnavailable()) {
            return 'customizedexports_export_channels_load_error';
        }
        return this.channelsTickedWithoutModels()
            ? 'customizedexports_export_models_required'
            : 'customizedexports_export_channels_required';
    });

    private readonly restoredScopeUnchanged = computed(() => {
        if (this.scopeAwaitingRestore() === null) {
            return false;
        }
        const restored = new Set(this.superTypesAwaitingRestore());
        const selected = this.selectedSuperTypes();
        return (
            selected.length === restored.size &&
            selected.every(superTypeNo => restored.has(superTypeNo))
        );
    });

    private readonly channelsTickedWithoutModels = computed(() => {
        const scope = this.meterScope();
        return (
            scope.channels.some(channel => channel.selected) &&
            !scope.models.some(model => model.selected)
        );
    });

    private readonly sftpDeliveryUnsettled = computed(() => {
        const schedule = this.scheduleValue();
        return (
            this.deliversOnSchedule() &&
            schedule.deliveryChannel === 'SFTP' &&
            schedule.sftpConnectionId === null
        );
    });

    private readonly holdsAdvancedDelivery = injectHoldsAdvancedDelivery();

    private readonly sftpDeliveryWithoutService = computed(
        () =>
            this.deliversOnSchedule() &&
            this.scheduleValue().deliveryChannel === 'SFTP' &&
            this.holdsAdvancedDelivery() !== true,
    );

    readonly deliversOnSchedule = computed(() => {
        const usage = this.selectedUsage();
        return usage === 'schedule' || usage === 'customized';
    });

    readonly shapesReadings = computed(() => this.selectedUsage() === 'customized');

    readonly readingResolution = signal<ReadingResolution>(this.stored.readingResolution);

    readonly daysBackInTimeControl = this.formBuilder.control<number | null>(
        this.stored.daysBackInTime,
        [
            Validators.required,
            AppValidators.integer,
            Validators.min(minDaysBackInTime),
            Validators.max(maxDaysBackInTime),
        ],
    );

    readonly daysBackInTime = toSignal(this.daysBackInTimeControl.valueChanges, {
        initialValue: this.daysBackInTimeControl.value,
    });

    private readonly readingShape = computed(() =>
        this.shapesReadings()
            ? {readingResolution: this.readingResolution(), daysBackInTime: this.daysBackInTime()}
            : {readingResolution: null, daysBackInTime: null},
    );

    private readonly daysBackInTimeStatus = toSignal(this.daysBackInTimeControl.statusChanges, {
        initialValue: this.daysBackInTimeControl.status,
    });

    readonly readingShapeInvalid = computed(() => this.daysBackInTimeStatus() === 'INVALID');

    readonly includeDismountedMetersControl = this.formBuilder.control<boolean>(
        this.stored.includeDismountedMeters,
    );

    readonly includeDismountedMeters = toSignal(this.includeDismountedMetersControl.valueChanges, {
        initialValue: this.includeDismountedMetersControl.value,
    });

    readonly selectedFormat = signal(
        exportFormats.find(format => format.id === this.stored.formatId) ?? csvExportFormat,
    );

    readonly fileFormat: ExportFileFormatForm = exportFileFormatForm(
        this.formBuilder,
        this.stored.fileFormat,
        () => this.selectedFormat().outputFormat === 'CSV',
    );

    readonly headerRow = signal<HeaderRowMode>(this.stored.headerRow);

    readonly unitPlacement = signal<UnitPlacement>(this.stored.unitPlacement);

    readonly columns = signal<ReadonlyArray<ExportColumnState>>(this.stored.columns);

    // The dismounted date is a column of the file only while the export carries dismounted meters.
    // It stays in `columns` either way, so its place in the order and its header survive unticking
    // the option and ticking it again.
    private readonly configuredColumns = computed(() =>
        this.includeDismountedMeters()
            ? this.columns()
            : this.columns().filter(column => column.id !== dismountedColumnId),
    );

    readonly visibleColumns = computed(() =>
        this.unitPlacement() === 'OWN_COLUMN'
            ? this.configuredColumns()
            : this.configuredColumns().filter(column => column.id !== 'unit'),
    );

    // The dismounted column leaves the configuration whenever the export stops carrying dismounted
    // meters, so it can never be the one column the lock keeps the file from losing - locking it
    // would let unticking the option empty the file.
    private readonly lastIncludedColumnId = computed(() => {
        const included = this.visibleColumns().filter(
            column => column.included && column.id !== dismountedColumnId,
        );
        return included.length === 1 ? included[0].id : null;
    });

    private readonly unitIsFoldedIntoValue = computed(
        () => this.unitPlacement() === 'FOLDED_INTO_VALUE',
    );

    readonly meterValueIsIncluded = computed(
        () => this.columns().find(column => column.id === 'meterValue')?.included === true,
    );

    private readonly lockedColumnIds = computed<ReadonlySet<ExportColumnId>>(() => {
        const ids = new Set<ExportColumnId>();
        const last = this.lastIncludedColumnId();
        if (last !== null) {
            ids.add(last);
        }
        if (this.unitIsFoldedIntoValue() && this.meterValueIsIncluded()) {
            ids.add('meterValue');
        }
        return ids;
    });

    readonly orderedColumns = computed<ReadonlyArray<OrderedExportColumn>>(() => {
        let position = 0;
        return this.visibleColumns().map(column => ({
            ...column,
            orderNumber: column.included ? ++position : null,
            locked: this.lockedColumnIds().has(column.id),
            labelKey:
                column.id === 'meterValue' && this.unitIsFoldedIntoValue()
                    ? meterValueAndUnitLabelKey
                    : exportColumnFields[column.id].labelKey,
            headingKey: exportColumnHeadingKey(column.id, this.unitPlacement()),
        }));
    });

    private readonly columnsWithoutFoldedUnit = computed<ReadonlyArray<ExportColumnState>>(() =>
        this.configuredColumns().map(column =>
            column.id === 'unit' && this.unitIsFoldedIntoValue()
                ? {...column, included: false}
                : column,
        ),
    );

    readonly selectedSystemIds = signal<string[]>(this.stored.systemIds);

    private readonly systemsRequired = computed(() => this.selectedSystemIds().length === 0);

    readonly autoIncludeFutureSystemsControl = this.formBuilder.control<boolean>(
        this.stored.autoIncludeFutureSystems,
    );

    readonly autoIncludeFutureSystems = toSignal(
        this.autoIncludeFutureSystemsControl.valueChanges,
        {initialValue: this.autoIncludeFutureSystemsControl.value},
    );

    readonly form: ExportForm = this.formBuilder.group({
        name: this.formBuilder.control<string | null>(
            this.stored.name === '' ? null : this.stored.name,
            // A draft restored from local storage skips the input's own maxlength.
            [AppValidators.noEmptyStringValidator, Validators.maxLength(maxSavedExportNameLength)],
        ),
        timeZone: this.formBuilder.control<string>(this.stored.timeZone, Validators.required),
        schedule: exportScheduleGroup(this.formBuilder, scheduleFormValue(this.stored)),
    });

    private readonly nameValue = toSignal(this.form.controls.name.valueChanges, {
        initialValue: this.form.controls.name.value,
    });

    readonly name = computed(() => this.nameValue() ?? '');

    private readonly timeZoneValue = toSignal(this.form.controls.timeZone.valueChanges, {
        initialValue: this.form.controls.timeZone.value,
    });

    private readonly scheduleValue = toSignal(
        this.form.controls.schedule.valueChanges.pipe(
            map(() => this.form.controls.schedule.getRawValue()),
        ),
        {initialValue: this.form.controls.schedule.getRawValue()},
    );

    private readonly boundFileFormat = bindExportFileFormat(this.fileFormat, () =>
        this.fileCarriesNumbers(),
    );

    readonly fileFormatSettings = this.boundFileFormat.settings;

    readonly exportsDecimalsAsStored = this.boundFileFormat.exportsDecimalsAsStored;

    // Baseline for the default-name prefill: only a draft whose name was empty at restore time
    // (and still is) gets the prefill, so a deliberate clear is never overwritten.
    private readonly savedSnapshot = signal<SavedExportDraft>(this.stored);

    private readonly defaultName = defaultExportName(() =>
        this.translate('customizedexports_export_default_name', 'online', 'export'),
    );

    private readonly chosenColumns = computed<ReadonlyArray<PreviewColumnChoice>>(() =>
        this.orderedColumns()
            .filter(column => column.included)
            .map(({id, header}) => ({id, header})),
    );

    private readonly previewValueKinds = computed(
        () => new Set(this.chosenColumns().map(column => formattedValueKind(column.id))),
    );

    readonly fileCarriesNumbers = computed(() => this.previewValueKinds().has('number'));

    readonly fileCarriesDates = computed(() => this.previewValueKinds().has('date'));

    readonly fileCarriesTimestamps = computed(() => this.previewValueKinds().has('timestamp'));

    readonly previewContent = computed<FilePreviewContent>(() =>
        previewContent(
            this.selectedFormat().outputFormat,
            {
                columns: this.chosenColumns(),
                headerRow: this.headerRow(),
                unitPlacement: this.unitPlacement(),
                fileFormat: this.fileFormatSettings(),
            },
            (key, application, module) => this.translate(key, application, module),
        ),
    );

    readonly previewFileName = computed(() => {
        const name = this.name();
        if (name === '') {
            return null;
        }
        const startDate = this.scheduleValue().startDate;
        return savedExportFileName(
            name,
            startDate ?? DateTime.now().startOf('day'),
            this.selectedFormat().outputFormat,
        );
    });

    private readonly deliveredReadingShape = computed(() => {
        const shape = this.readingShape();
        const schedule = this.scheduleValue();
        const endedMonths =
            this.deliversOnSchedule() &&
            schedule.customSchedule &&
            schedule.scheduleUnit === 'MONTHS' &&
            schedule.schedulePeriod === 'ENDED_MONTHS';
        return endedMonths && shape.daysBackInTime !== null
            ? {
                  ...shape,
                  daysBackInTime:
                      endedMonthsWindowDays(schedule.scheduleInterval, DateTime.utc()) ??
                      shape.daysBackInTime,
              }
            : shape;
    });

    private readonly sftpConnectionRemoval = sftpConnectionRemoval(this.form.controls.schedule);

    readonly sftpPreviewFileName = computed(() => {
        const name = this.name();
        if (name === '') {
            return null;
        }
        const {startDate, fileNamePattern} = this.scheduleValue();
        const firstDelivery = startDate ?? DateTime.now().startOf('day');
        return sftpDeliveryFileName(
            fileNamePattern,
            name,
            this.shapesReadings() ? firstDelivery.minus({days: 1}) : firstDelivery,
            this.selectedFormat().outputFormat,
            DateTime.now(),
        );
    });

    private readonly readingLimits = computed<ExportLimits>(() =>
        this.readingShapeInvalid()
            ? noExportLimits
            : exportLimits({
                  ...this.deliveredReadingShape(),
                  systemCount: this.selectedSystemIds().length,
                  channelCount: this.storedChannels().length,
                  outputFormat: this.selectedFormat().outputFormat,
                  deliversByEmail:
                      this.deliversOnSchedule() && this.scheduleValue().deliveryChannel === 'EMAIL',
              }),
    );

    readonly exportLimits = computed<ExportLimits>(() => ({
        findings: [
            ...this.readingLimits().findings,
            ...sftpConnectionRemovedLimits(
                this.sftpConnectionRemoval.removed() && this.deliversOnSchedule(),
                this.scheduleValue(),
            ).findings,
        ],
    }));

    private readonly meterTypesStatus = computed<StepStatus>(() => {
        if (!this.meterSelectionBlocked()) {
            return 'filled';
        }
        return this.saveAttempted() ? 'invalid' : 'empty';
    });

    private readonly systemsStatus = computed<StepStatus>(() => {
        if (!this.systemsRequired()) {
            return 'filled';
        }
        return this.saveAttempted() ? 'invalid' : 'empty';
    });

    private readonly nameStatus = controlStepStatus(this.form.controls.name);

    private readonly readingShapeStatus = controlStepStatus(this.daysBackInTimeControl);

    private readonly fileFormatStatus = controlStepStatus(this.fileFormat);

    private readonly scheduleStatus = controlStepStatus(this.form.controls.schedule);

    readonly stepStatuses = computed(
        () =>
            new Map<FlowSlot, StepStatus>([
                ['name', this.nameStatus()],
                ['readingShape', this.readingShapeStatus()],
                ['meterTypes', this.meterTypesStatus()],
                ['systems', this.systemsStatus()],
                ['format', this.fileFormatStatus()],
                ['schedule', this.scheduleStatus()],
            ]),
    );

    constructor() {
        effect(() => {
            const draft = this.currentDraft();
            untracked(() => {
                if (!this.isEditing()) {
                    writeStoredDraft(draft);
                }
            });
        });

        // Prefill the default name once translations are ready. Only while both the draft and its
        // baseline are still empty, so a deliberate clear is never overwritten, and the baseline
        // moves along so the prefill alone does not count as an unsaved change.
        effect(() => {
            const defaultName = this.defaultName();
            untracked(() => {
                if (defaultName !== '' && this.name() === '' && this.savedSnapshot().name === '') {
                    this.form.controls.name.setValue(defaultName);
                    this.savedSnapshot.update(snapshot => ({...snapshot, name: defaultName}));
                }
            });
        });

        // The schedule half only exists on the schedule path; disabled it stays out of validity
        // while the on-demand path saves (AGI-19086 user flow 1).
        effect(() => {
            if (this.deliversOnSchedule()) {
                this.form.controls.schedule.enable();
            } else {
                this.form.controls.schedule.disable();
            }
        });

        effect(() => {
            if (this.shapesReadings()) {
                this.daysBackInTimeControl.enable();
            } else {
                this.daysBackInTimeControl.disable();
            }
        });

        effect(() => {
            const available = this.availableSuperTypes();
            if (available == null) {
                return;
            }
            this.retainSelected(
                this.selectedSuperTypes,
                available.map(superType => superType.meterSuperTypeNo),
            );
        });

        effect(() => {
            const available = this.availableChannels();
            if (available == null) {
                return;
            }
            const superTypes = untracked(this.selectedSuperTypes);
            const awaitingRestore = untracked(this.scopeAwaitingRestore);
            if (awaitingRestore !== null) {
                this.scopeAwaitingRestore.set(null);
                this.restoreMeterScope(available, superTypes, awaitingRestore);
                return;
            }
            this.resolveStoredModelKeys(available, superTypes);
            this.reconcileMeterScope(meterScopeAvailability(available, superTypes));
        });

        effect(() => {
            const systems = this.relevantSystems();
            if (systems == null || this.selectedSuperTypes().length === 0) {
                return;
            }
            const systemNos = systems.map(system => system.systemNo);
            const offeredBefore = untracked(this.offeredSystemNos);
            this.offeredSystemNos.set(systemNos);
            this.retainSelected(this.selectedSystemIds, systemNos);
            if (untracked(this.isEditing) && untracked(this.autoIncludeFutureSystems)) {
                this.selectAllSystems(
                    systemNos.filter(systemNo => !offeredBefore.includes(systemNo)),
                );
            }
            if (untracked(this.systemsAwaitingAutoInclude)) {
                this.systemsAwaitingAutoInclude.set(false);
                this.savedSnapshot.update(snapshot => ({
                    ...snapshot,
                    systemIds: untracked(this.selectedSystemIds),
                }));
            }
        });
    }

    selectFormatById(id: string): void {
        const format = exportFormats.find(candidate => candidate.id === id);
        if (format !== undefined) {
            this.selectedFormat.set(format);
        }
    }

    toggleSuperType(meterSuperTypeNo: string): void {
        this.selectedSuperTypes.update(current =>
            current.includes(meterSuperTypeNo)
                ? current.filter(superType => superType !== meterSuperTypeNo)
                : [...current, meterSuperTypeNo],
        );
    }

    selectAllSuperTypes(): void {
        this.selectedSuperTypes.set(
            (this.availableSuperTypes() ?? []).map(superType => superType.meterSuperTypeNo),
        );
    }

    clearSuperTypes(): void {
        this.selectedSuperTypes.set([]);
    }

    private retainSelected(target: WritableSignal<string[]>, availableIds: string[]): void {
        target.update(current => {
            const retained = retainAvailable(current, availableIds);
            return retained.length === current.length ? current : retained;
        });
    }

    toggleModel(modelId: string): void {
        this.selectedModelIds.update(current => toggleId(current, modelId));
    }

    selectModels(modelIds: ReadonlyArray<string>): void {
        this.selectedModelIds.update(current => addIds(current, modelIds));
    }

    deselectModels(modelIds: ReadonlyArray<string>): void {
        this.selectedModelIds.update(current => removeIds(current, modelIds));
    }

    toggleChannel(channelId: string): void {
        this.selectedChannelIds.update(current => toggleId(current, channelId));
    }

    selectChannels(channelIds: ReadonlyArray<string>): void {
        this.selectedChannelIds.update(current => addIds(current, channelIds));
    }

    deselectChannels(channelIds: ReadonlyArray<string>): void {
        this.selectedChannelIds.update(current => removeIds(current, channelIds));
    }

    toggleChannelLess(meterSuperTypeNo: string): void {
        this.selectedChannelLess.update(current => ({
            ...current,
            [meterSuperTypeNo]: !isChannelLessTicked(current, meterSuperTypeNo),
        }));
    }

    private restoreMeterScope(
        available: ReadonlyArray<CustomizedExportSuperTypeChannels>,
        superTypes: ReadonlyArray<string>,
        storedChannels: ReadonlyArray<SavedExportChannel>,
    ): void {
        const restored = meterScopeSelectionFromChannels(available, superTypes, storedChannels);
        const availability = meterScopeAvailability(available, superTypes);
        this.selectedModelIds.set(restored.modelIds);
        this.selectedChannelIds.set(restored.channelIds);
        this.offeredModelIds.set(availability.modelIds);
        this.offeredChannelIds.set(availability.channelIds);
        this.savedSnapshot.set(this.currentDraft());
    }

    private resolveStoredModelKeys(
        available: ReadonlyArray<CustomizedExportSuperTypeChannels>,
        superTypes: ReadonlyArray<string>,
    ): void {
        const stored = untracked(this.storedModelKeys);
        if (stored === null) {
            return;
        }
        this.storedModelKeys.set(null);
        this.selectedModelIds.set(modelIdsForKeys(available, superTypes, stored.selected));
        this.offeredModelIds.set(modelIdsForKeys(available, superTypes, stored.offered));
    }

    private reconcileMeterScope(availability: MeterScopeAvailability): void {
        const modelsOfferedBefore = untracked(this.offeredModelIds);
        const channelsOfferedBefore = untracked(this.offeredChannelIds);

        this.selectedModelIds.update(current =>
            withNewlyOffered(current, availability.modelIds, modelsOfferedBefore, id =>
                availability.modelIds.includes(id),
            ),
        );
        this.selectedChannelIds.update(current =>
            withNewlyOffered(current, availability.channelIds, channelsOfferedBefore, id =>
                availability.primaryChannelIds.includes(id),
            ),
        );

        this.offeredModelIds.set(availability.modelIds);
        this.offeredChannelIds.set(availability.channelIds);
    }

    toggleSystem(systemNo: string): void {
        this.selectedSystemIds.update(current =>
            current.includes(systemNo)
                ? current.filter(id => id !== systemNo)
                : [...current, systemNo],
        );
    }

    // Merges instead of replacing: "select all" acts on the rows the step's column search currently
    // shows, and must not drop selections hidden by that filter.
    selectAllSystems(systemNos: string[]): void {
        this.selectedSystemIds.update(current => [
            ...current,
            ...systemNos.filter(systemNo => !current.includes(systemNo)),
        ]);
    }

    clearSystems(): void {
        this.selectedSystemIds.set([]);
    }

    // Mirrors selectAllSystems: removes only the rows the column search currently shows, so
    // unchecking "select all" while filtered cannot silently drop selections hidden by that filter.
    deselectSystems(systemNos: string[]): void {
        this.selectedSystemIds.update(current => current.filter(id => !systemNos.includes(id)));
    }

    moveVisibleColumn(fromIndex: number, toIndex: number): void {
        const visible = this.visibleColumns();
        const columns = [...this.columns()];
        moveItemInArray(
            columns,
            columns.indexOf(visible[fromIndex]),
            columns.indexOf(visible[toIndex]),
        );
        this.columns.set(columns);
    }

    selectUsage(id: string): void {
        setFromSchema(this.selectedUsage, savedExportUsageSchema, id);
    }

    selectReadingResolution(id: string): void {
        setFromSchema(this.readingResolution, readingResolutionSchema, id);
    }

    selectHeaderRow(id: string): void {
        setFromSchema(this.headerRow, headerRowModeSchema, id);
    }

    selectUnitPlacement(id: string): void {
        const selected = unitPlacementSchema.safeParse(id);
        if (!selected.success) {
            return;
        }
        if (selected.data === 'FOLDED_INTO_VALUE' && !this.meterValueIsIncluded()) {
            return;
        }
        this.unitPlacement.set(selected.data);
    }

    toggleColumn(id: ExportColumnId): void {
        if (this.lockedColumnIds().has(id)) {
            return;
        }
        this.columns.update(columns =>
            columns.map(column =>
                column.id === id ? {...column, included: !column.included} : column,
            ),
        );
    }

    renameColumn(id: ExportColumnId, header: string): void {
        const renamed = header.trim() === '' ? null : header;
        this.columns.update(columns =>
            columns.map(column => (column.id === id ? {...column, header: renamed} : column)),
        );
    }

    resetColumnsStep(): void {
        this.headerRow.set(emptySavedExportDraft.headerRow);
        this.unitPlacement.set(emptySavedExportDraft.unitPlacement);
        this.columns.set(defaultExportColumns);
    }

    resetFileFormatStep(): void {
        resetExportFileFormat(this.fileFormat);
    }

    reset(): void {
        const draft = {
            ...emptySavedExportDraft,
            name: this.defaultName(),
            startDate: DateTime.now().startOf('day').toISODate(),
        };
        this.form.reset({
            name: draft.name === '' ? null : draft.name,
            timeZone: draft.timeZone,
            schedule: scheduleFormValue(draft),
        });
        this.resetFileFormatStep();
        this.systemsAwaitingAutoInclude.set(false);
        this.offeredSystemNos.set([]);
        this.selectedUsage.set(draft.usage);
        this.readingResolution.set(draft.readingResolution);
        this.daysBackInTimeControl.setValue(draft.daysBackInTime);
        this.includeDismountedMetersControl.setValue(draft.includeDismountedMeters);
        this.selectedSuperTypes.set([...draft.superTypes]);
        this.storedModelKeys.set({selected: draft.modelKeys, offered: draft.offeredModelKeys});
        this.selectedModelIds.set([]);
        this.selectedChannelIds.set([...draft.channelIds]);
        this.offeredModelIds.set([]);
        this.offeredChannelIds.set([...draft.offeredChannelIds]);
        this.selectedChannelLess.set({...draft.channelLess});
        this.selectedSystemIds.set([...draft.systemIds]);
        this.autoIncludeFutureSystemsControl.setValue(draft.autoIncludeFutureSystems);
        this.selectedFormat.set(csvExportFormat);
        this.headerRow.set(draft.headerRow);
        this.unitPlacement.set(draft.unitPlacement);
        this.columns.set(draft.columns);
        this.savedSnapshot.set(draft);
        this.saveAttempted.set(false);
        this.sftpConnectionRemoval.clear();
    }

    loadFromSavedExport(savedExport: SavedExport): void {
        this.isEditing.set(true);
        this.form.patchValue({
            name: savedExport.name,
            timeZone: savedExport.timeZone ?? emptySavedExportDraft.timeZone,
            schedule: savedExportScheduleValue(savedExport),
        });
        this.sftpConnectionRemoval.load(savedExport);
        this.resetFileFormatStep();
        const format = exportFormats.find(
            candidate => candidate.outputFormat === savedExport.outputFormat,
        );
        this.selectedUsage.set(savedExportUsage(savedExport));
        this.readingResolution.set(
            savedExport.readingResolution ?? emptySavedExportDraft.readingResolution,
        );
        this.daysBackInTimeControl.setValue(
            savedExport.daysBackInTime ?? emptySavedExportDraft.daysBackInTime,
        );
        this.includeDismountedMetersControl.setValue(savedExport.includeDismountedMeters);
        this.selectedSuperTypes.set([...savedExport.superTypes]);
        this.storedModelKeys.set(null);
        this.selectedModelIds.set([]);
        this.selectedChannelIds.set([]);
        this.offeredModelIds.set([]);
        this.offeredChannelIds.set([]);
        this.scopeAwaitingRestore.set([...savedExport.channels]);
        this.superTypesAwaitingRestore.set([...savedExport.superTypes]);
        this.selectedChannelLess.set(
            Object.fromEntries(
                savedExport.superTypes.map(superTypeNo => [
                    superTypeNo,
                    savedExport.channelLessSuperTypes.includes(superTypeNo),
                ]),
            ),
        );
        this.selectedSystemIds.set([...savedExport.systemIds]);
        this.autoIncludeFutureSystemsControl.setValue(savedExport.autoIncludeFutureSystems);
        this.systemsAwaitingAutoInclude.set(savedExport.autoIncludeFutureSystems);
        this.offeredSystemNos.set([]);
        if (format !== undefined) {
            this.selectedFormat.set(format);
        }
        this.fileFormat.patchValue(exportFileFormatFormValue(savedExportFileFormat(savedExport)));
        this.headerRow.set(savedExport.headerRow);
        this.unitPlacement.set(savedExport.unitPlacement);
        this.columns.set(reconcileExportColumns(toExportColumnStates(savedExport.columns)));
        this.savedSnapshot.set(this.currentDraft());
        this.saveAttempted.set(false);
    }

    hasUnsavedChanges(): boolean {
        return JSON.stringify(this.currentDraft()) !== JSON.stringify(this.savedSnapshot());
    }

    validateForSave(): boolean {
        this.saveAttempted.set(true);
        if (
            this.form.invalid ||
            this.fileFormat.invalid ||
            this.readingShapeInvalid() ||
            this.meterSelectionBlocked() ||
            this.systemsRequired() ||
            this.sftpDeliveryWithoutService()
        ) {
            this.form.markAllAsTouched();
            this.fileFormat.markAllAsTouched();
            this.daysBackInTimeControl.markAsTouched();
            const blockedReason = this.saveBlockedReason();
            if (blockedReason !== null) {
                this.toastrService.error(this.translate(blockedReason, 'online', 'export'));
            }
            return false;
        }
        return true;
    }

    save(): void {
        if (this.isSaving() || !this.validateForSave()) {
            return;
        }
        this.isSaving.set(true);

        this.savedExportsService
            .createSavedExport(this.buildSaveRequest())
            .pipe(finalize(() => this.isSaving.set(false)))
            .subscribe({
                next: () => {
                    this.reset();
                    // The persistence effect never flushes before the navigation destroys this
                    // service, so the pre-save draft would survive in local storage.
                    clearStoredDraft();
                    this.toastrService.success(
                        this.translate('savedexport_create_success', 'online', 'export'),
                    );
                    void this.router.navigate(['admin', 'export', 'saved-exports']);
                },
                error: () => {
                    // HTTP interceptor surfaces the generic error toast
                },
            });
    }

    buildSaveRequest(): SavedExportCreateRequest {
        const {outputFormat} = this.selectedFormat();
        const scheduled = this.deliversOnSchedule();
        return {
            outputFormat,
            name: this.name(),
            exportType: this.selectedUsage() === 'customized' ? 'CUSTOMIZED' : 'QUICK',
            ...savedExportScheduleFields(scheduled, this.form.controls.schedule.getRawValue()),
            ...toSavedExportColumnConfig(
                this.headerRow(),
                this.unitPlacement(),
                this.columnsWithoutFoldedUnit(),
            ),
            timeZone: this.form.controls.timeZone.value,
            includeDismountedMeters: this.includeDismountedMeters(),
            ...this.fileFormatSettings(),
            ...this.readingShape(),
            ...this.scopeFields(),
        };
    }

    validateForTest(): boolean {
        return this.validateForSave();
    }

    buildTestRequest(): SavedExportTestRequest {
        const {outputFormat} = this.selectedFormat();
        return {
            outputFormat,
            name: this.name(),
            exportType: this.selectedUsage() === 'customized' ? 'CUSTOMIZED' : 'QUICK',
            ...savedExportScheduleFields(true, this.form.controls.schedule.getRawValue()),
            timeZone: this.timeZoneValue(),
            includeDismountedMeters: this.includeDismountedMeters(),
            ...this.fileFormatSettings(),
            ...this.readingShape(),
            ...toSavedExportColumnConfig(
                this.headerRow(),
                this.unitPlacement(),
                this.columnsWithoutFoldedUnit(),
            ),
            ...this.scopeFields(),
        };
    }

    private scopeFields(): Pick<
        SavedExportCreateRequest,
        | 'superTypes'
        | 'channelLessSuperTypes'
        | 'channels'
        | 'systemIds'
        | 'autoIncludeFutureSystems'
    > {
        return {
            superTypes: this.selectedSuperTypes().map(Number),
            channelLessSuperTypes: includedChannelLessSuperTypes(
                this.availableChannels(),
                this.selectedSuperTypes(),
                this.selectedChannelLess(),
            ).map(Number),
            channels: this.storedChannels().flatMap(toSavedExportChannelRequest),
            systemIds: this.selectedSystemIds().map(Number),
            autoIncludeFutureSystems: this.autoIncludeFutureSystems(),
        };
    }

    private currentDraft(): SavedExportDraft {
        const storedModelKeys = this.storedModelKeys();
        return {
            // The untouched prefill stores as empty, so the next visit prefills in the visitor's
            // current language instead of replaying a name frozen in another language.
            name: this.name() === this.defaultName() ? '' : this.name(),
            usage: this.selectedUsage(),
            ...scheduleDraftValue(this.scheduleValue()),
            superTypes: this.selectedSuperTypes(),
            modelKeys: storedModelKeys?.selected ?? modelKeysOf(this.selectedModelIds()),
            channelIds: this.selectedChannelIds(),
            offeredModelKeys: storedModelKeys?.offered ?? modelKeysOf(this.offeredModelIds()),
            offeredChannelIds: this.offeredChannelIds(),
            channelLess: this.selectedChannelLess(),
            systemIds: this.selectedSystemIds(),
            autoIncludeFutureSystems: this.autoIncludeFutureSystems(),
            formatId: this.selectedFormat().id,
            fileFormat: this.fileFormatSettings(),
            headerRow: this.headerRow(),
            unitPlacement: this.unitPlacement(),
            columns: [...this.columns()],
            timeZone: this.timeZoneValue(),
            includeDismountedMeters: this.includeDismountedMeters(),
            readingResolution: this.readingResolution(),
            daysBackInTime: this.daysBackInTime(),
        };
    }
}

function modelKeysOf(modelIds: ReadonlyArray<string>): string[] {
    return [...new Set(modelIds.map(meterModelKeyOf))];
}

function toggleId(current: ReadonlyArray<string>, id: string): string[] {
    return current.includes(id) ? current.filter(candidate => candidate !== id) : [...current, id];
}

function addIds(current: ReadonlyArray<string>, ids: ReadonlyArray<string>): string[] {
    const added = ids.filter(id => !current.includes(id));
    return added.length === 0 ? [...current] : [...current, ...added];
}

function removeIds(current: ReadonlyArray<string>, ids: ReadonlyArray<string>): string[] {
    return current.filter(id => !ids.includes(id));
}

function retainAvailable(
    current: ReadonlyArray<string>,
    availableIds: ReadonlyArray<string>,
): string[] {
    return current.filter(id => availableIds.includes(id));
}

function withNewlyOffered(
    current: ReadonlyArray<string>,
    offeredNow: ReadonlyArray<string>,
    offeredBefore: ReadonlyArray<string>,
    ticksByDefault: (id: string) => boolean,
): string[] {
    const retained = retainAvailable(current, offeredNow);
    const newlyOffered = offeredNow.filter(
        id => !offeredBefore.includes(id) && !retained.includes(id) && ticksByDefault(id),
    );
    return [...retained, ...newlyOffered];
}

function toExportColumnStates(
    columns: ReadonlyArray<SavedExportColumn>,
): ReadonlyArray<ExportColumnState> {
    return columns.flatMap(column => {
        const id = exportColumnIdSchema.safeParse(column.columnId);
        return id.success ? [{id: id.data, included: column.included, header: column.header}] : [];
    });
}

function setFromSchema<T extends string>(
    target: WritableSignal<T>,
    schema: z.ZodType<T>,
    id: string,
): void {
    const selected = schema.safeParse(id);
    if (selected.success) {
        target.set(selected.data);
    }
}

export const customizedExportFormProviders: Provider[] = [
    CustomizedExportsFormService,
    {provide: EXPORT_FORM, useFactory: (): ExportForm => inject(CustomizedExportsFormService).form},
    ...exportTestProviders(CustomizedExportsFormService),
];
